import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FiSkipForward, FiX, FiRotateCcw, FiList, FiPlay } from "react-icons/fi";
import { maskUserData } from "../WinnersModal";
import { createParticles, stepParticle, transformFor } from "./pileSim";
import "./ReceiptDrawStage.css";

const RECEIPT_W = 320;
const LINE_H = 18.6;
const CHARS = 36; // monospace characters that fit on one receipt line

/* The first few receipts get the full show, the rest move quicker */
const FULL_SHOW_COUNT = 3;
const TIMING = {
  full: { shuffle: 2600, settle: 2400, lift: 950, flip: 750, line: 70, hold: 2600, stow: 700 },
  fast: { shuffle: 1400, settle: 1600, lift: 600, flip: 450, line: 28, hold: 1200, stow: 480 },
};

/* Zig-zag torn edge on top and bottom of the receipt */
const zigzag = (teeth = 22, depth = 6) => {
  const n = teeth * 2;
  const pts = [];
  for (let i = 0; i <= n; i++) pts.push(`${((i / n) * 100).toFixed(3)}% ${i % 2 ? 0 : depth}px`);
  for (let i = n; i >= 0; i--) pts.push(`${((i / n) * 100).toFixed(3)}% calc(100% - ${i % 2 ? 0 : depth}px)`);
  return `polygon(${pts.join(",")})`;
};
const ZIGZAG = zigzag();

const center = (text) => {
  const t = String(text).slice(0, CHARS);
  const pad = Math.max(0, Math.floor((CHARS - t.length) / 2));
  return " ".repeat(pad) + t;
};
const row = (left, right) => {
  const r = String(right);
  const l = String(left).slice(0, CHARS - r.length - 1);
  return l + " ".repeat(Math.max(1, CHARS - l.length - r.length)) + r;
};

/* Winners come from the lab's dummy data or from a real draw. A real winner only has
   what the API could give: items, totals and the business name may be missing, and
   cashback winners have no receipt at all (just their receipt count and amount). */
const receiptLines = (w) => {
  const items = w.items || [];
  const hasTotal = w.total !== null && w.total !== undefined && w.total !== "";
  const vat = w.vat ?? (Number(w.total) * 0.18) / 1.18;
  const lines = [
    { kind: "bold", text: center("ATK · LOTARIA FISKALE") },
    { kind: "text", text: center(w.business_name || "Biznes") },
    { kind: "text", text: center(`NUI ${w.business_nui || "—"}`) },
    ...(w.business_city ? [{ kind: "text", text: center(w.business_city) }] : []),
    { kind: "rule" },
  ];

  if (w.receipt_id) {
    lines.push({ kind: "text", text: row("Kuponi", `#${w.receipt_id}`) });
    if (w.coupon_id) lines.push({ kind: "text", text: row("Nr. kuponit", w.coupon_id) });
    if (w.receipt_type) lines.push({ kind: "text", text: row("Lloji", w.receipt_type) });
    lines.push({ kind: "text", text: row("Data", w.receipt_date || "—") });
  } else {
    lines.push({ kind: "text", text: row("Përdoruesi", `#${w.user_id ?? "—"}`) });
    if (w.receipt_count != null) lines.push({ kind: "text", text: row("Kupona", w.receipt_count) });
  }

  if (items.length) {
    lines.push(
      { kind: "rule" },
      ...items.map((i) => ({
        kind: "text",
        text: row(`${i.name} x${i.qty}`, Number(i.amount ?? i.qty * i.price).toFixed(2)),
      }))
    );
  }

  if (hasTotal) {
    lines.push(
      { kind: "rule" },
      { kind: "bold", text: row("TOTAL", `${Number(w.total).toFixed(2)} €`) },
      { kind: "text", text: row("TVSH", Number(vat).toFixed(2)) }
    );
  }

  return lines;
};

const useViewport = () => {
  const [size, setSize] = useState({ w: window.innerWidth, h: window.innerHeight });
  useEffect(() => {
    const onResize = () => setSize({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return size;
};

const fullName = (m) => [m.first_name, m.last_name].filter(Boolean).join(" ") || "Fitues";

const prizeLabel = (w) =>
  w.prize_amount !== null && w.prize_amount !== undefined && w.prize_amount !== ""
    ? `${w.prize_amount}€`
    : w.prize_desc || "";

/* "ID 12 · Kuponi #345 · 200€", skipping the parts a winner doesn't have */
const winnerMeta = (w, ...parts) =>
  [w.user_id != null && `ID ${w.user_id}`, w.receipt_id && `Kuponi #${w.receipt_id}`, ...parts]
    .filter(Boolean)
    .join(" · ");

function Receipt({ winner, lines, printing, stamped, lineDelay, startDelay }) {
  return (
    <div className="rd-shadow">
      <div
        className={`rd-paper ${printing ? "is-printing" : ""} ${stamped ? "is-thud" : ""}`}
        style={{ clipPath: ZIGZAG }}
      >
        {lines.map((l, i) => {
          const style = { "--delay": `${startDelay + i * lineDelay}ms` };
          if (l.kind === "rule") return <div key={i} className="rd-line rd-rule" style={style} />;
          return (
            <div key={i} className={`rd-line ${l.kind === "bold" ? "font-bold" : ""}`} style={style}>
              {l.text}
            </div>
          );
        })}

        <div className={`rd-ring ${stamped ? "is-on" : ""}`} />
        <div className={`rd-stamp ${stamped ? "is-on" : ""}`}>
          <div className="text-3xl font-black tracking-[0.18em] leading-none">FITUES</div>
          <div className="text-[10px] font-bold tracking-widest mt-1">
            {winner.tier_level ? `KUSHTI ${winner.tier_level} · ` : ""}
            {prizeLabel(winner)}
          </div>
        </div>
      </div>
    </div>
  );
}

/* Props:
   - winners: the drawn rows, or null while a real draw is still running (the pile keeps
     shuffling until they arrive)
   - onStart: when given, the stage opens on a "ready" screen and calls it on confirm —
     this is where a real draw is started
   - onReplay / onShowList: optional buttons on the summary
   - businessWinners: the businesses behind winning receipts that also won (lottery draws);
     when given, the summary splits into "Qytetarët" and "Bizneset" tabs
   - isBusiness: winners are businesses — show their names unmasked
   - demo: show the DEMO badge */
export default function ReceiptDrawStage({
  winners: winnersProp = [],
  totalReceipts = 10000,
  subtitle,
  readyTitle = "Gjenero fituesit?",
  readyText,
  onStart,
  onClose,
  onReplay,
  onShowList,
  businessWinners,
  isBusiness = false,
  demo = false,
}) {
  const winners = useMemo(() => winnersProp || [], [winnersProp]);
  const winnersRef = useRef(winnersProp);
  winnersRef.current = winnersProp;
  const { w: vw, h: vh } = useViewport();
  const isLg = vw >= 1024;
  const isSmall = vw < 640;
  const pileSize = isSmall ? 300 : isLg ? 800 : 500;
  const slipW = isSmall ? 22 : 32;
  const slipH = Math.round(slipW * 2);

  const [phase, setPhase] = useState(onStart ? "ready" : "intro"); // (ready →) intro → shuffle → settle → grab → lift → print → stamp → stow → … → done
  const [current, setCurrent] = useState(0);
  const [pileReady, setPileReady] = useState(false);
  const [gone, setGone] = useState([]);
  const [pickIdx, setPickIdx] = useState(null);
  const [drawn, setDrawn] = useState([]);
  const [count, setCount] = useState(0);
  const [bin, setBin] = useState({ w: 0, h: 0 });
  const binRef = useRef(null);
  const partsRef = useRef(null); // particle state lives outside React, written straight to the DOM
  const slipEls = useRef([]);
  const phaseRef = useRef(phase);
  const goneRef = useRef(gone);
  phaseRef.current = phase;
  goneRef.current = gone;

  const reduced = useMemo(() => window.matchMedia?.("(prefers-reduced-motion: reduce)").matches, []);

  useEffect(() => {
    const el = binRef.current;
    if (!el) return;
    const measure = () => setBin({ w: el.clientWidth, h: el.clientHeight });
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  /* Create the pile once the bin has a size; rescale it on resize */
  const lastBin = useRef(null);
  useEffect(() => {
    if (!bin.w || !bin.h) return;
    if (!partsRef.current) {
      partsRef.current = createParticles(pileSize, bin.w, bin.h, { poured: reduced, slipH });
      setPileReady(true);
    } else if (lastBin.current) {
      const kx = bin.w / lastBin.current.w;
      const ky = bin.h / lastBin.current.h;
      partsRef.current.forEach((p, i) => {
        p.x *= kx;
        p.y *= ky;
        const el = slipEls.current[i];
        if (el) el.style.transform = transformFor(p);
      });
    }
    lastBin.current = bin;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bin]);

  /* Physics loop: blower on during "shuffle", off otherwise; idles once everything has landed */
  useEffect(() => {
    if (!pileReady || reduced) return;
    let raf;
    let last = performance.now();
    let t = 0;
    let air = 0;
    const loop = (now) => {
      const dt = Math.min(0.033, (now - last) / 1000);
      last = now;
      t += dt;
      const target = phaseRef.current === "shuffle" ? 1 : 0;
      air += (target - air) * Math.min(1, dt * (target ? 5 : 7));
      if (!target && air < 0.02) air = 0;

      const parts = partsRef.current;
      const { w, h } = lastBin.current;
      const goneIds = goneRef.current;
      for (let i = 0; i < parts.length; i++) {
        const p = parts[i];
        if (p.resting && air === 0) continue;
        if (goneIds.includes(p.id)) continue;
        stepParticle(p, dt, t, air, w, h, slipW, slipH);
        const el = slipEls.current[i];
        if (el) el.style.transform = transformFor(p);
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pileReady]);
  const masked = useMemo(() => (isBusiness ? winners : maskUserData(winners)), [winners, isBusiness]);
  const hasBusinessTab = Array.isArray(businessWinners) && !isBusiness;
  const [summaryTab, setSummaryTab] = useState("citizens");
  const T = reduced || current >= FULL_SHOW_COUNT ? TIMING.fast : TIMING.full;

  const winner = winners[current];
  const lines = useMemo(() => (winner ? receiptLines(winner) : []), [winner]);

  /* Lock page scroll while the stage is up */
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  /* The sequence — one timer per phase, cleaned up on every step */
  useEffect(() => {
    if (!winner && !["intro", "shuffle", "done"].includes(phase)) return;
    let t;
    let iv;
    let raf1;
    let raf2;

    switch (phase) {
      case "intro": {
        const start = performance.now();
        const tick = (now) => {
          const p = Math.min(1, (now - start) / 1300);
          setCount(Math.round(totalReceipts * (1 - Math.pow(1 - p, 3))));
          if (p < 1) raf1 = requestAnimationFrame(tick);
          else t = setTimeout(() => setPhase("shuffle"), 300);
        };
        raf1 = requestAnimationFrame(tick);
        break;
      }
      case "shuffle": {
        /* The physics loop turns the blower on while we're in this phase.
           A real draw keeps shuffling until the API has answered. */
        const next = () => {
          if (!winnersRef.current?.length) return;
          clearInterval(iv);
          setPhase("settle");
        };
        t = setTimeout(() => {
          next();
          iv = setInterval(next, 200);
        }, reduced ? 300 : T.shuffle);
        break;
      }
      case "settle": {
        /* Blower off — wait for the pile to land (or give up after T.settle),
           then take a slip from the top layer of the mound */
        const started = performance.now();
        const pick = () => {
          const parts = partsRef.current || [];
          const inBin = parts.filter((p) => !gone.includes(p.id));
          const landed = inBin.filter((p) => p.resting);
          if (landed.length < inBin.length && performance.now() - started < T.settle) return;
          clearInterval(iv);
          const pool = (landed.length ? landed : inBin).sort((a, b) => b.u - a.u);
          const top = pool.slice(0, Math.max(1, Math.ceil(pool.length * 0.25)));
          setPickIdx(top[Math.floor(Math.random() * top.length)].id);
          setPhase("grab");
        };
        iv = setInterval(pick, 100);
        break;
      }
      case "grab":
        /* Hero mounts on top of the chosen slip; two frames later it starts flying */
        raf1 = requestAnimationFrame(() => {
          raf2 = requestAnimationFrame(() => {
            setGone((g) => [...g, pickIdx]);
            setPhase("lift");
          });
        });
        break;
      case "lift":
        t = setTimeout(() => setPhase("print"), T.lift);
        break;
      case "print":
        t = setTimeout(() => setPhase("stamp"), T.flip * 0.6 + lines.length * T.line + 350);
        break;
      case "stamp":
        t = setTimeout(() => setPhase("stow"), T.hold);
        break;
      case "stow":
        t = setTimeout(() => {
          setDrawn((d) => [...d, current]);
          if (current + 1 < winners.length) {
            setCurrent((c) => c + 1);
            setPhase("shuffle");
          } else {
            setPhase("done");
          }
        }, T.stow);
        break;
      default:
        break;
    }

    return () => {
      clearTimeout(t);
      clearInterval(iv);
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, current]);

  const skip = () => {
    setDrawn(winners.map((_, i) => i));
    setPhase("done");
  };

  /* ---------- Hero placement ---------- */
  const mainW = vw - (isLg ? 300 : 0);
  const mainH = vh - 60 - (isLg ? 0 : 84);
  const heroH = 48 + lines.length * LINE_H + 110; // paper + winner tag
  const heroScale = Math.max(0.45, Math.min(1, (mainH * 0.94) / heroH, (mainW * 0.9) / RECEIPT_W));

  const pickSlip = partsRef.current?.find((p) => p.id === pickIdx);
  const heroVisible = ["grab", "lift", "print", "stamp", "stow"].includes(phase);
  let heroStyle = {};
  if (pickSlip && phase === "grab") {
    /* Bin box is 4%/4% inset and the bottom 42% of the stage */
    heroStyle = {
      left: `${4 + (pickSlip.x / bin.w) * 92}%`,
      top: `${58 + (pickSlip.y / bin.h) * 42}%`,
      transform: `translate(-50%, -50%) scale(${(slipW * pickSlip.scale) / RECEIPT_W}) rotate(${pickSlip.a}deg)`,
      "--d": "0ms",
    };
  } else if (phase === "lift") {
    heroStyle = {
      left: "50%",
      top: "46%",
      transform: `translate(-50%, -50%) scale(${heroScale * 0.72}) rotate(-5deg)`,
      "--d": `${T.lift}ms`,
    };
  } else if (phase === "print" || phase === "stamp") {
    heroStyle = {
      left: "50%",
      /* Nudge up so the winner tag hanging below the receipt stays on screen */
      top: `calc(50% - ${Math.round(55 * heroScale)}px)`,
      transform: `translate(-50%, -50%) scale(${heroScale})`,
      "--d": `${T.flip}ms`,
    };
  } else if (phase === "stow") {
    heroStyle = isLg
      ? { left: "112%", top: "18%", transform: "translate(-50%, -50%) scale(0.22) rotate(16deg)", opacity: 0, "--d": `${T.stow}ms` }
      : { left: "50%", top: "130%", transform: "translate(-50%, -50%) scale(0.3) rotate(10deg)", opacity: 0, "--d": `${T.stow}ms` };
  }

  const start = () => {
    setPhase("intro");
    onStart?.();
  };

  const status = {
    ready: "Gati për tërheqje",
    intro: "Po ngarkohen kuponat…",
    shuffle: winnersProp ? "Duke përzier kuponat…" : "Duke gjeneruar fituesit…",
    settle: "Duke përzier kuponat…",
    grab: "Po tërhiqet një kupon…",
    lift: "Po tërhiqet një kupon…",
    print: winner ? `Kuponi #${winner.receipt_id}` : "",
    stamp: "Fitues!",
    stow: "Fitues!",
    done: "Tërheqja përfundoi",
  }[phase];

  const printing = ["print", "stamp", "stow"].includes(phase);
  const stamped = ["stamp", "stow"].includes(phase);
  const inPile = phase === "intro" ? count : Math.max(0, totalReceipts - gone.length);

  const stage = (
    <div className="rd-stage fixed inset-0 z-[60] flex flex-col text-white overflow-hidden" role="dialog" aria-label="Tërheqja nga kuponat">
      {/* Top bar */}
      <div className="relative z-30 h-[60px] shrink-0 flex items-center justify-between gap-3 px-4 sm:px-6 border-b border-white/10">
        <div className="flex items-center gap-2 min-w-0">
          <span className="rd-live-dot inline-block w-2.5 h-2.5 rounded-full bg-red-500 shrink-0" />
          <span className="text-[11px] font-bold tracking-[0.2em] uppercase text-red-300">Live</span>
          {demo && (
            <span className="rounded bg-amber-400 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-[#021766]">Demo</span>
          )}
          <span className="text-xs sm:text-sm text-white/75 truncate hidden sm:inline">{subtitle}</span>
        </div>
        <div className="text-xs sm:text-sm font-semibold text-[#F3DCA8] truncate text-center">{status}</div>
        <div className="flex items-center gap-2 shrink-0">
          {phase !== "done" && phase !== "ready" && winners.length > 0 && (
            <button
              type="button"
              onClick={skip}
              className="inline-flex items-center gap-1.5 rounded-lg border border-white/20 bg-white/5 hover:bg-white/15 px-3 py-1.5 text-xs font-semibold transition"
            >
              Kalo <FiSkipForward size={13} />
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            aria-label="Mbyll"
            className="inline-flex items-center justify-center w-8 h-8 rounded-lg hover:bg-white/10 transition"
          >
            <FiX size={18} />
          </button>
        </div>
      </div>

      <div className="flex-1 flex min-h-0">
        {/* Main stage */}
        <div className="relative flex-1 min-w-0 overflow-hidden">
          <div className={`rd-spot ${heroVisible ? "is-on" : ""}`} />

          {/* Pile counter */}
          <div className="absolute left-4 sm:left-6 top-4 z-10">
            <div className="text-[10px] sm:text-[11px] font-semibold uppercase tracking-[0.25em] text-white/45">Kupona në short</div>
            <div className="text-2xl sm:text-4xl font-black tabular-nums text-white">{inPile.toLocaleString("de-DE")}</div>
          </div>
          <div className="absolute right-4 sm:right-6 top-4 z-10 text-right">
            <div className="text-[10px] sm:text-[11px] font-semibold uppercase tracking-[0.25em] text-white/45">Fituesi</div>
            <div className="text-2xl sm:text-4xl font-black tabular-nums">
              {winners.length ? String(Math.min(current + 1, winners.length)).padStart(2, "0") : "—"}
              <span className="text-white/35 text-lg sm:text-2xl">
                {" "}/ {winners.length ? String(winners.length).padStart(2, "0") : "—"}
              </span>
            </div>
          </div>

          {/* Bin with the pile of receipts */}
          <div
            ref={binRef}
            className={`rd-bin ${heroVisible && phase !== "grab" ? "is-dim" : ""} ${phase === "done" ? "is-dim" : ""}`}
          >
            <div className="rd-bin-back" />
            {pileReady &&
              partsRef.current.map((p, i) => (
                <div
                  key={p.id}
                  ref={(el) => {
                    slipEls.current[i] = el;
                    if (el && !el.style.transform) el.style.transform = transformFor(p);
                  }}
                  className={`rd-slip ${gone.includes(p.id) || (phase === "grab" && p.id === pickIdx) ? "is-gone" : ""}`}
                  style={{
                    "--sw": `${slipW}px`,
                    "--sh": `${slipH}px`,
                    "--paper": p.paper,
                    zIndex: Math.round(p.z * 100),
                  }}
                />
              ))}
            <div className="rd-bin-front z-[200]" />
          </div>

          {/* The pulled receipt */}
          {heroVisible && winner && (
            <div key={current} className="rd-hero" style={heroStyle}>
              <div className={`rd-card ${phase === "grab" || phase === "lift" ? "is-back" : ""}`} style={{ "--flip": `${T.flip}ms` }}>
                <div className="rd-face">
                  <Receipt
                    winner={winner}
                    lines={lines}
                    printing={printing}
                    stamped={stamped}
                    lineDelay={T.line}
                    startDelay={T.flip * 0.6}
                  />
                </div>
                <div className="rd-face rd-face-back">
                  <div className="rd-shadow h-full">
                    <div className="rd-paper rd-paper-back" style={{ clipPath: ZIGZAG }} />
                  </div>
                </div>
              </div>

              {stamped && (
                <div className="absolute left-0 right-0 top-full mt-5 flex justify-center">
                  <div className="rd-rise w-[300px] rounded-2xl border border-[#D2AB67]/40 bg-[#010a33]/85 px-5 py-3 text-center shadow-2xl">
                    <div className="text-[10px] font-semibold uppercase tracking-[0.3em] text-[#D2AB67]">Fituesi</div>
                    <div className="text-2xl font-bold tracking-wide mt-0.5">{fullName(masked[current] || {})}</div>
                    <div className="mt-1 text-xs text-white/60 tabular-nums">
                      {winnerMeta(winner, prizeLabel(winner))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Ready: confirm before the real draw starts */}
          {phase === "ready" && (
            <div className="absolute inset-x-0 top-[14%] z-40 flex justify-center p-4">
              <div className="rd-rise w-full max-w-md rounded-2xl border border-white/15 bg-[#010a33]/90 p-5 sm:p-6 text-center shadow-2xl">
                <div className="text-[11px] font-semibold uppercase tracking-[0.3em] text-[#D2AB67]">{subtitle}</div>
                <div className="text-xl sm:text-2xl font-bold mt-1">{readyTitle}</div>
                {readyText && <p className="mt-2 text-sm text-white/70 leading-relaxed">{readyText}</p>}
                <div className="mt-5 flex flex-col sm:flex-row gap-2">
                  <button
                    type="button"
                    onClick={start}
                    className="flex-1 inline-flex items-center justify-center gap-2 rounded-lg bg-[#D2AB67] hover:bg-[#D2AB67]/90 px-4 py-2.5 text-sm font-bold text-[#021766] transition"
                  >
                    <FiPlay size={14} /> Fillo tërheqjen
                  </button>
                  <button
                    type="button"
                    onClick={onClose}
                    className="flex-1 rounded-lg border border-white/25 hover:bg-white/10 px-4 py-2.5 text-sm font-semibold transition"
                  >
                    Anulo
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Summary */}
          {phase === "done" && (
            <div className="absolute inset-0 z-40 flex items-center justify-center p-4">
              <div className="rd-rise w-full max-w-2xl rounded-2xl border border-white/15 bg-[#010a33]/90 p-5 sm:p-6 shadow-2xl">
                <div className="text-center">
                  <div className="text-[11px] font-semibold uppercase tracking-[0.3em] text-[#D2AB67]">Tërheqja përfundoi</div>
                  <div className="text-xl sm:text-2xl font-bold mt-1">
                    {winners.length} {isBusiness ? "fitues" : "kupona fitues"} nga {totalReceipts.toLocaleString("de-DE")}
                  </div>
                </div>
                {hasBusinessTab && (
                  <div className="mt-5 flex justify-center">
                    <div className="inline-flex rounded-lg border border-white/15 bg-white/5 p-1 gap-1">
                      {[
                        { key: "citizens", label: "Qytetarët", count: winners.length },
                        { key: "businesses", label: "Bizneset", count: businessWinners.length },
                      ].map((t) => (
                        <button
                          key={t.key}
                          type="button"
                          onClick={() => setSummaryTab(t.key)}
                          className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-md text-sm font-semibold transition ${
                            summaryTab === t.key ? "bg-[#D2AB67] text-[#021766]" : "text-white/70 hover:text-white"
                          }`}
                        >
                          {t.label}
                          <span
                            className={`rounded-full px-1.5 text-[11px] tabular-nums ${
                              summaryTab === t.key ? "bg-[#021766]/15" : "bg-white/10"
                            }`}
                          >
                            {t.count}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {hasBusinessTab && summaryTab === "businesses" ? (
                  businessWinners.length === 0 ? (
                    <div className="mt-4 py-10 text-center text-sm text-white/50">
                      Asnjë biznes nuk u shpall fitues në këtë tërheqje.
                    </div>
                  ) : (
                    <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2.5 max-h-[45vh] overflow-y-auto">
                      {businessWinners.map((b, i) => (
                        <div key={i} className="rd-mini rounded-lg px-3 py-2 font-mono text-xs" style={{ animationDelay: `${i * 60}ms` }}>
                          <div className="flex justify-between font-bold">
                            <span>NUI {b.business_nui || "—"}</span>
                            <span>{prizeLabel(b)}</span>
                          </div>
                          <div className="font-sans text-sm font-semibold mt-0.5 truncate">
                            {b.business_name || "Biznes"}
                          </div>
                          <div className="text-[11px] text-gray-500 truncate">
                            {b.receipt_id ? `Kuponi #${b.receipt_id}` : ""}
                          </div>
                        </div>
                      ))}
                    </div>
                  )
                ) : (
                  <div className={`${hasBusinessTab ? "mt-4" : "mt-5"} grid grid-cols-1 sm:grid-cols-2 gap-2.5 max-h-[45vh] overflow-y-auto`}>
                    {winners.map((w, i) => (
                      <div key={i} className="rd-mini rounded-lg px-3 py-2 font-mono text-xs" style={{ animationDelay: `${i * 60}ms` }}>
                        <div className="flex justify-between font-bold">
                          <span>{w.receipt_id ? `#${w.receipt_id}` : `ID ${w.user_id ?? "—"}`}</span>
                          <span>{prizeLabel(w)}</span>
                        </div>
                        <div className="font-sans text-sm font-semibold mt-0.5">{fullName(masked[i] || {})}</div>
                        <div className="text-[11px] text-gray-500 truncate">
                          {[w.user_id != null && `ID ${w.user_id}`, w.business_name].filter(Boolean).join(" · ")}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                <div className="mt-5 flex flex-col sm:flex-row gap-2">
                  {onReplay && (
                    <button
                      type="button"
                      onClick={onReplay}
                      className="flex-1 inline-flex items-center justify-center gap-2 rounded-lg bg-[#D2AB67] hover:bg-[#D2AB67]/90 px-4 py-2.5 text-sm font-bold text-[#021766] transition"
                    >
                      <FiRotateCcw size={14} /> Përsërit
                    </button>
                  )}
                  {onShowList && (
                    <button
                      type="button"
                      onClick={onShowList}
                      className="flex-1 inline-flex items-center justify-center gap-2 rounded-lg bg-[#D2AB67] hover:bg-[#D2AB67]/90 px-4 py-2.5 text-sm font-bold text-[#021766] transition"
                    >
                      <FiList size={14} /> Hap listën e fituesve
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={onClose}
                    className="flex-1 rounded-lg border border-white/25 hover:bg-white/10 px-4 py-2.5 text-sm font-semibold transition"
                  >
                    Mbyll
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Winners rail (desktop) */}
        <aside className="hidden lg:flex w-[300px] shrink-0 flex-col border-l border-white/10 bg-black/20">
          <div className="px-5 pt-5 pb-3 text-[11px] font-semibold uppercase tracking-[0.25em] text-white/45">
            Kuponat fitues
          </div>
          <div className="flex-1 overflow-y-auto px-4 pb-4 flex flex-col gap-2.5">
            {drawn.length === 0 && <div className="text-xs text-white/30 px-1">Ende asnjë</div>}
            {[...drawn].reverse().map((i) => (
              <div key={i} className="rd-mini rounded-lg px-3 py-2 font-mono text-xs shadow-lg">
                <div className="flex justify-between font-bold">
                  <span>{winners[i].receipt_id ? `#${winners[i].receipt_id}` : `ID ${winners[i].user_id ?? "—"}`}</span>
                  <span className="text-[#b3261e]">FITUES</span>
                </div>
                <div className="font-sans text-sm font-semibold mt-0.5">{fullName(masked[i] || {})}</div>
                <div className="text-[11px] text-gray-500">
                  {[winners[i].user_id != null && `ID ${winners[i].user_id}`, prizeLabel(winners[i])].filter(Boolean).join(" · ")}
                </div>
              </div>
            ))}
          </div>
        </aside>
      </div>

      {/* Winners strip (mobile / tablet) */}
      <div className="lg:hidden h-[84px] shrink-0 border-t border-white/10 bg-black/25 px-4 flex items-center gap-2 overflow-x-auto">
        {drawn.length === 0 && <span className="text-xs text-white/30">Kuponat fitues do të shfaqen këtu</span>}
        {drawn.map((i) => (
          <div key={i} className="rd-mini shrink-0 rounded-md px-2.5 py-1.5 font-mono text-[11px]">
            <div className="font-bold">{winners[i].receipt_id ? `#${winners[i].receipt_id}` : `ID ${winners[i].user_id ?? "—"}`}</div>
            <div className="font-sans font-semibold">{fullName(masked[i] || {})}</div>
          </div>
        ))}
      </div>
    </div>
  );

  return createPortal(stage, document.body);
}
